"use client";

import React from "react";
import { v3, inkAccent } from "@/lib/theme";
import { fmtEur } from "@/lib/data/mock";
import { V3SectionHead } from "./V3SectionHead";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export function V3SectionZinsen() {
  const myRate = 4.20;
  const bestRate = 3.10;
  const avgRate = 3.65;
  const balance = 215000; // Restschuld of the affected object
  const ersparnisProJahr = balance * (myRate - bestRate) / 100;

  const [dismissed, setDismissed] = React.useState(false);
  const ink = inkAccent();

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px", background: v3.surf2, borderTop: `1px solid ${v3.line}`, borderBottom: `1px solid ${v3.line}` }} className="rp-section"
    >
      <V3SectionHead n={5} kicker="Nie wieder zu viel Zinsen zahlen"
        title="Dein Geld."
        dimWord="Nicht das der Bank."
        sub="realpilot scannt täglich den österreichischen Markt. Sobald du günstiger refinanzieren kannst, bekommst du Bescheid — nicht erst beim Anschlussfinanzierungstermin in drei Jahren." />

      <div className="rp-stack" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32, alignItems: "start" }}>
        {/* Notification + bars */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {!dismissed && (
            <div style={{
              background: v3.intense ? v3.accentBg : v3.surf,
              border: `1px solid ${v3.accent}`, borderRadius: 14,
              padding: "20px 24px", display: "flex", alignItems: "flex-start", gap: 16,
              boxShadow: v3.intense ? `0 0 0 6px ${v3.accentBg}, 0 16px 60px -20px ${v3.accentDim}` : "none",
            }}>
              <div className="v3-pulse animate-pulse" style={{ width: 10, height: 10, borderRadius: 99, background: v3.accent, marginTop: 8, boxShadow: `0 0 12px ${v3.accent}`, flex: "0 0 10px" }} />
              <div style={{ flex: 1 }}>
                <div style={{ font: `11px ${v3.mono}`, color: v3.accent, letterSpacing: "0.08em", textTransform: "uppercase" }}>Zinsalarm · Heute 09:14</div>
                <div style={{ font: `500 22px ${v3.sans}`, color: v3.fg, marginTop: 6, letterSpacing: "-0.02em" }}>
                  Objekt 02 · Anschlussfinanzierung in 18 Monaten
                </div>
                <div style={{ font: `14px ${v3.sans}`, color: v3.fg2, marginTop: 6 }}>
                  Dein Zinssatz: <strong style={{ color: v3.fg }}>{myRate.toFixed(2).replace(".", ",")} %</strong> · Aktuell verfügbar (Erste Bank, BAWAG, Raiffeisen Ø): <strong style={{ color: v3.accent }}>{bestRate.toFixed(2).replace(".", ",")} %</strong>
                </div>
                <div style={{ font: `14px ${v3.sans}`, color: v3.fg, marginTop: 6 }}>
                  Mögliche Ersparnis: <span style={{ color: v3.accent, fontWeight: 600 }}>{fmtEur(ersparnisProJahr, { decimals: 0 })} / Jahr</span> · {fmtEur(ersparnisProJahr * 5, { decimals: 0 })} über 5 Jahre.
                </div>
              </div>
              <button onClick={() => setDismissed(true)} style={{ background: "transparent", color: v3.dim, border: 0, font: `16px ${v3.sans}`, cursor: "pointer", padding: 4 }}>×</button>
            </div>
          )}

          {/* Rate comparison bars */}
          <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 14, padding: 28, overflowX: "auto" }}>
            <div style={{ minWidth: 400 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 24 }}>
                <span style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>Zinsen · Marktvergleich</span>
                <span style={{ font: `11px ${v3.mono}`, color: v3.dim }}>Stand heute · 12 Banken</span>
              </div>

              {[
                { label: "Dein Zinssatz",     rate: myRate,   tone: "warn",   sub: "Erste Bank · Vertrag 2023" },
                { label: "Marktdurchschnitt", rate: avgRate,  tone: "dim",    sub: "Ø über 12 Banken" },
                { label: "Marktbest heute",   rate: bestRate, tone: "accent", sub: "BAWAG · 25 Jahre fix" },
              ].map((b, i) => {
                const pct = (b.rate / 5.5) * 100;
                const color = b.tone === "accent" ? ink : b.tone === "warn" ? v3.fg2 : v3.dim;
                return (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "150px 1fr 80px", gap: 16, alignItems: "center", padding: "14px 0", borderTop: i > 0 ? `1px solid ${v3.line}` : 0 }}>
                    <div>
                      <div style={{ font: `14px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>{b.label}</div>
                      <div style={{ font: `11px ${v3.mono}`, color: v3.dim }}>{b.sub}</div>
                    </div>
                    <div style={{ height: 14, background: v3.line2, borderRadius: 99, position: "relative", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 99, transition: "width .6s cubic-bezier(.2,.7,.3,1)" }} />
                    </div>
                    <div style={{ textAlign: "right", font: `500 18px ${v3.mono}`, color }}>
                      {b.rate.toFixed(2).replace(".", ",")} %
                    </div>
                  </div>
                );
              })}

              <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${v3.line}`, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button>
                  Vergleichsangebot anfordern
                </Button>
                <Button variant="outline">
                  Alle 12 Banken ansehen
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Umschuldungs-Alert detail */}
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 14, padding: 28, display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <div style={{ font: `11px ${v3.mono}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Ersparnis-Rechnung</div>
            <div style={{ font: `500 72px/0.95 ${v3.sans}`, color: ink, marginTop: 8, letterSpacing: "-0.04em" }}>
              {fmtEur(ersparnisProJahr * 10, { decimals: 0 })}
            </div>
            <div style={{ font: `14px ${v3.sans}`, color: v3.dim, marginTop: 6 }}>über 10 Jahre · bei Umschuldung jetzt</div>
          </div>

          <div style={{ borderTop: `1px solid ${v3.line}`, paddingTop: 18 }}>
            <div style={{ font: `12px ${v3.sans}`, color: v3.dim, marginBottom: 12 }}>So setzt sich das zusammen</div>
            {[
              ["Restschuld Objekt 02", fmtEur(balance)],
              ["Differenz Zinssatz",   `−${(myRate - bestRate).toFixed(2).replace(".", ",")} %`],
              ["Ersparnis Jahr 1",     fmtEur(ersparnisProJahr, { decimals: 0, sign: true })],
              ["Ersparnis Jahr 1–5",   fmtEur(ersparnisProJahr * 5, { decimals: 0, sign: true })],
              ["Ersparnis Jahr 1–10",  fmtEur(ersparnisProJahr * 10, { decimals: 0, sign: true })],
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderTop: i > 0 ? `1px solid ${v3.line}` : 0, font: `13px ${v3.mono}`, color: v3.fg2 }}>
                <span style={{ color: v3.dim }}>{r[0]}</span>
                <span style={{ color: i >= 2 ? ink : v3.fg }}>{r[1]}</span>
              </div>
            ))}
          </div>

          <div style={{ background: v3.bg, border: `1px solid ${v3.line}`, borderRadius: 10, padding: "14px 16px", font: `12px ${v3.mono}`, color: v3.dim }}>
            <div style={{ color: v3.accent, marginBottom: 4 }}>► Nächster Check: morgen 06:00</div>
            realpilot prüft täglich. Du wirst nur informiert, wenn sich etwas ändert.
          </div>
        </div>
      </div>
    </motion.section>
  );
}
